package com.snakesworld.game.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.snakesworld.game.network.MultiplayerRepository
import com.snakesworld.game.network.isEphemeralUserId
import com.snakesworld.game.network.UserProfile
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import kotlin.time.Duration.Companion.milliseconds

sealed interface ConnectingStatus {
    data object Connecting : ConnectingStatus
    data class Connected(val assignedUserId: String) : ConnectingStatus
    data class Error(val message: String) : ConnectingStatus
}

data class ConnectingUiState(
    val status: ConnectingStatus = ConnectingStatus.Connecting,
    val progress: Float = 0.2f,
)

class ConnectingViewModel(
    private val repository: MultiplayerRepository,
    private val customScope: CoroutineScope? = null,
) : ViewModel() {

    private val scope: CoroutineScope
        get() = customScope ?: viewModelScope

    private val _uiState = MutableStateFlow(ConnectingUiState())
    val uiState: StateFlow<ConnectingUiState> = _uiState.asStateFlow()

    fun connect(callsign: String, skinId: String, userId: String = "") {
        scope.launch {
            _uiState.value = ConnectingUiState(ConnectingStatus.Connecting, progress = 0.3f)
            delay(50.milliseconds)
            try {
                val authenticatedUid = (repository.authState.value as? com.snakesworld.game.network.AuthState.Authenticated)?.uid
                    ?: run {
                        val initRes = repository.initializeAndSignInAnonymously()
                        initRes.getOrNull()
                    }
                val effectiveUid = authenticatedUid?.takeIf { it.isNotBlank() }
                    ?: userId.takeIf { !isEphemeralUserId(it) }
                    ?: userId
                val existing = if (effectiveUid.isNotEmpty()) repository.getUserProfile(effectiveUid) else null
                val profile = (existing ?: UserProfile(uid = effectiveUid)).copy(
                    uid = effectiveUid,
                    nickname = callsign.ifBlank { existing?.nickname ?: "PLAYER_01" }
                )
                val assignedId = repository.joinArena(profile, skinId)
                _uiState.value = _uiState.value.copy(progress = 0.85f)
                try {
                    withTimeoutOrNull(800L.milliseconds) {
                        repository.pellets.first { it.isNotEmpty() }
                    }
                } catch (_: Throwable) {}

                _uiState.value = ConnectingUiState(
                    status = ConnectingStatus.Connected(assignedId),
                    progress = 1.0f
                )
            } catch (e: Exception) {
                _uiState.value = ConnectingUiState(
                    status = ConnectingStatus.Error(e.message ?: "Failed to connect to battle arena"),
                    progress = 0f
                )
            }
        }
    }

    fun reset() {
        _uiState.value = ConnectingUiState()
    }
}

